import { useEffect, useRef, useState } from 'react';
import { ArrowUpRight, Check, Copy } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { buildPilotSummary, buildWhatsAppUrl, validatePilotForm } from '../lib/contact';
import { getProductBySlug } from '../data/products';

const productOptions = ['Flow', 'Check', 'Memory', 'Not sure yet'];
const productFromSearch = (search) => getProductBySlug(new URLSearchParams(search).get('product'))?.shortName || 'Not sure yet';

const PilotForm = () => {
  const { search } = useLocation();
  const [values, setValues] = useState({ name: '', email: '', business: '', businessType: '', product: productFromSearch(search), need: '' });
  const [errors, setErrors] = useState({});
  const [summary, setSummary] = useState('');
  const [handoffUrl, setHandoffUrl] = useState('');
  const [copied, setCopied] = useState(false);
  const [copyError, setCopyError] = useState('');
  const errorRef = useRef(null);

  useEffect(() => {
    setValues((current) => ({ ...current, product: productFromSearch(search) }));
  }, [search]);

  const update = ({ target }) => {
    setValues((current) => ({ ...current, [target.name]: target.value }));
    setSummary('');
    setHandoffUrl('');
    setCopyError('');
  };
  const submit = (event) => {
    event.preventDefault();
    const nextErrors = validatePilotForm(values);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) {
      requestAnimationFrame(() => errorRef.current?.focus());
      return;
    }
    const nextSummary = buildPilotSummary(values);
    const nextUrl = buildWhatsAppUrl(values);
    setSummary(nextSummary);
    setHandoffUrl(nextUrl);
    window.open(nextUrl, '_blank', 'noopener,noreferrer');
  };

  const copySummary = async () => {
    const currentSummary = buildPilotSummary(values);
    setSummary(currentSummary);
    setCopyError('');
    try {
      await navigator.clipboard.writeText(currentSummary);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch {
      setCopyError('Clipboard access is unavailable. Select and copy the summary below.');
    }
  };

  const field = (name, label, type = 'text', placeholder = '') => (
    <label className="form-field" htmlFor={name}>
      <span>{label}</span>
      <input id={name} name={name} type={type} value={values[name]} onChange={update} placeholder={placeholder} aria-invalid={Boolean(errors[name])} aria-describedby={errors[name] ? `${name}-error` : undefined} />
      {errors[name] && <small id={`${name}-error`} role="alert">{errors[name]}</small>}
    </label>
  );

  return (
    <section id="pilot" className="pilot-section section-dark">
      <div className="pilot-copy">
        <p className="eyebrow">Start with one workflow</p>
        <h2>What would you like to make easier?</h2>
        <p>Tell us about a workflow your team still handles manually. We will explore whether one of our products could be relevant for a focused pilot.</p>
        <div className="pilot-note"><span>01</span><p>This starts an exploratory conversation. Pilot scope and availability will be discussed together.</p></div>
      </div>
      <form className="pilot-form" onSubmit={submit} noValidate>
        {Object.keys(errors).length > 0 && (
          <div className="error-summary" role="alert" tabIndex="-1" ref={errorRef}>
            <strong>Complete {Object.keys(errors).length} highlighted fields.</strong>
            <p>Review the fields marked below.</p>
          </div>
        )}
        <div className="form-grid">
          {field('name', 'Name', 'text', 'Your full name')}
          {field('email', 'Business email', 'email', 'you@company.com')}
          {field('business', 'Business name', 'text', 'Company or business name')}
          {field('businessType', 'Business type', 'text', 'Example: food distribution')}
        </div>
        <label className="form-field" htmlFor="product">
          <span>Product interest</span>
          <select id="product" name="product" value={values.product} onChange={update}>
            {productOptions.map((option) => <option key={option}>{option}</option>)}
          </select>
        </label>
        <label className="form-field" htmlFor="need">
          <span>Describe one workflow you want to improve</span>
          <textarea id="need" name="need" rows="5" value={values.need} onChange={update} placeholder="Example: our team re-enters chat orders every afternoon…" aria-invalid={Boolean(errors.need)} aria-describedby={errors.need ? 'need-error' : undefined} />
          {errors.need && <small id="need-error" role="alert">{errors.need}</small>}
        </label>
        <p className="form-helper">After review, your summary opens in WhatsApp so you can decide whether to send it.</p>
        <div className="form-actions">
          <button className="button button--light" type="submit">Request a Pilot Discussion <ArrowUpRight aria-hidden="true" /></button>
          <button className="button button--ghost" type="button" onClick={copySummary}>{copied ? <Check aria-hidden="true" /> : <Copy aria-hidden="true" />}{copied ? 'Copied' : 'Copy summary'}</button>
        </div>
        {summary && (
          <div className="summary-panel" aria-live="polite">
            <label htmlFor="pilot-summary">Your prepared summary</label>
            <textarea id="pilot-summary" readOnly value={summary} rows="9" />
            {copyError && <p role="alert">{copyError}</p>}
            <p>Your summary is ready. If WhatsApp did not open, use the link below or copy the text manually.</p>
            {handoffUrl && <a href={handoffUrl} target="_blank" rel="noreferrer">Open WhatsApp with this summary <ArrowUpRight aria-hidden="true" /></a>}
          </div>
        )}
      </form>
    </section>
  );
};

export default PilotForm;
